/**
 * Payment-source panel for TransactionDetailModal.
 *
 * Renders payer + payee in side-by-side cards with an arrow indicator,
 * the amount in both K-INR and ₹INR, method, note, and both legs of
 * the payment. Each leg has an "Open this party's ledger" link.
 */
import { ArrowRight } from 'lucide-react';
import type { EntryDetail, LedgerEntry, LedgerSource } from '../../api';
import { kinrToInrDisplay } from '../../money/inr';
import { Button } from '../../ui/primitives';

interface Props {
  detail: EntryDetail;
  source: Extract<LedgerSource, { kind: 'payment' }>;
  currentPartyId: number;
  onOpenOtherParty: (partyId: number, entryId: number) => void;
}

export function PaymentPanel({
  detail,
  source,
  currentPartyId,
  onOpenOtherParty,
}: Props) {
  const { payment, bothLegs } = source;
  const parties = detail.parties;
  const payer = parties[String(payment.payerPartyId)];
  const payee = parties[String(payment.payeePartyId)];

  return (
    <div className="space-y-4">
      {/* Payer → Payee card */}
      <div className="rounded-md border border-border bg-surface-raised p-3">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <PartyCard label="Payer" name={payer?.displayName ?? '—'} role={payer?.role ?? ''} />
          <ArrowRight className="h-5 w-5 text-ink-muted" strokeWidth={2} />
          <PartyCard label="Payee" name={payee?.displayName ?? '—'} role={payee?.role ?? ''} />
        </div>
      </div>

      {/* Facts grid */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-small">
        <dt className="text-ink-muted">Amount</dt>
        <dd className="text-right font-mono text-ink">
          {payment.amount} K-INR
          <span className="ml-2 text-caption text-ink-muted">
            ({kinrToInrDisplay(payment.amount)})
          </span>
        </dd>
        <dt className="text-ink-muted">Method</dt>
        <dd className="text-right text-ink">{payment.method.replace('_', ' ')}</dd>
        <dt className="text-ink-muted">Via self (3-party)</dt>
        <dd className="text-right text-ink">{payment.viaSelf ? 'Yes' : 'No'}</dd>
        {payment.note && (
          <>
            <dt className="text-ink-muted">Note</dt>
            <dd className="text-right text-ink">{payment.note}</dd>
          </>
        )}
        {payment.voidedAt && (
          <>
            <dt className="text-ink-muted">Voided</dt>
            <dd className="text-right text-warn">
              {payment.voidedAt.slice(0, 10)} — {payment.voidedReason}
            </dd>
          </>
        )}
        <dt className="text-ink-muted">Posted</dt>
        <dd className="text-right text-caption text-ink-muted">{payment.createdAt}</dd>
      </dl>

      {/* Both legs */}
      <div>
        <h3 className="mb-2 text-small font-semibold text-ink">Both legs</h3>
        <ul className="space-y-2.5">
          {bothLegs.map((leg) => (
            <LegRow
              key={leg.id}
              leg={leg}
              partyName={parties[String(leg.partyId)]?.displayName ?? '—'}
              isCurrent={leg.partyId === currentPartyId}
              onOpen={() => onOpenOtherParty(leg.partyId, leg.id)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function PartyCard({ label, name, role }: { label: string; name: string; role: string }) {
  return (
    <div className="rounded-md border border-border bg-surface p-2.5">
      <p className="text-caption uppercase tracking-wide text-ink-muted">{label}</p>
      <p className="mt-0.5 truncate text-small font-semibold text-ink">{name}</p>
      <p className="text-caption text-ink-muted">{role}</p>
    </div>
  );
}

function LegRow({
  leg,
  partyName,
  isCurrent,
  onOpen,
}: {
  leg: LedgerEntry;
  partyName: string;
  isCurrent: boolean;
  onOpen: () => void;
}) {
  const hasDr = leg.dr !== '0';
  const hasCr = leg.cr !== '0';
  return (
    <li
      className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-small ${
        isCurrent ? 'border-sec-ledgers bg-sec-ledgers-tint' : 'border-border bg-surface'
      }`}
    >
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="truncate font-medium text-ink">{partyName}</span>
        {isCurrent && (
          <span className="shrink-0 text-caption text-ink-muted">(this ledger)</span>
        )}
      </div>
      <div className="flex shrink-0 items-baseline gap-3">
        {hasDr && (
          <span className="font-mono">
            <span className="text-ink-muted">Dr </span>
            <span className="text-ink">{leg.dr}</span>
          </span>
        )}
        {hasCr && (
          <span className="font-mono">
            <span className="text-warn">Cr </span>
            <span className="text-warn">{leg.cr}</span>
          </span>
        )}
        {!isCurrent && (
          <Button size="sm" variant="ghost" onClick={onOpen}>
            Open →
          </Button>
        )}
      </div>
    </li>
  );
}
